import { Telemetry, ValidTelemetryEvent } from '~commonInfra/services/Telemetry';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

/**
 * PFT (Product Feature Telemetry) event fired once per Task Details page view.
 *
 * Backs Epic 264 [SPAM] "Show more details on Jobs Page" PRD §2.10 / Success
 * Measures, which require a page-view metric for Task Details usage (target:
 * 25% of Jobs Page admins view Task Details). Without an explicit emit that
 * metric cannot be measured.
 *
 * Wire event name: `task_details_page_view`
 * Org ID type:     `tenant_id`
 *
 * Emitted from `TaskDetailsSpaceProvider`'s effect keyed on `taskLuid`, so it
 * fires once per distinct task viewed -- including task-to-task navigation,
 * where React Router reuses the provider instance rather than remounting it
 * (a mount-only effect would emit only for the first task and undercount view
 * volume). The provider only renders when the already feature-gated Task
 * Details route resolves -- see ServerSpaceRouter / SiteSpaceRouter -- so no
 * provider-level flag check is needed here.
 *
 * PII discipline: the payload carries ONLY the event `type` and the
 * `tenant_id` Standard Attribute. It intentionally omits `task_luid`, content
 * names, owner/creator emails, schedule, and every other free-text or
 * identifying field from the Task Details contract. A per-tenant page-view
 * count is sufficient for the success measure, and additional categorical
 * dimensions (e.g. task type) are deferred until the downstream Juno/Endeavour
 * PFT owner approves them and reflects them in the registered schema.
 *
 * NOTE: the generic `Telemetry.sendEvent<T>()` path is used deliberately --
 * NOT the Admin-Insights emitter, which gates on Admin-Insights *project
 * membership* (`isAdminInsightsContent`) and would silently no-op here.
 */
export const TASK_DETAILS_PAGE_VIEW_EVENT_TYPE = 'task_details_page_view';

export interface TaskDetailsPageViewTelemetryEvent extends ValidTelemetryEvent {
  /** Wire event name. Always `task_details_page_view`. */
  type: typeof TASK_DETAILS_PAGE_VIEW_EVENT_TYPE;
  /**
   * PFT Standard Attribute for tenant routing. On Tableau Cloud the tenant is
   * the site, so this equals `site_luid`. Set per-event here (rather than on
   * `Telemetry.getCommonProperties()`) so non-PFT vpc events owned by other
   * teams don't inherit a Standard Attribute their schemas don't declare --
   * matching the catalog EAD telemetry helpers.
   */
  tenant_id: string;
}

/**
 * Emit the `task_details_page_view` PFT event for the current tenant.
 *
 * Fire-and-forget: `Telemetry.sendEvent` self-swallows any failure, so this is
 * safe to call from a render-effect without a try/catch at the call site.
 */
export function sendTaskDetailsPageViewTelemetry(): void {
  Telemetry.sendEvent<TaskDetailsPageViewTelemetryEvent>({
    type: TASK_DETAILS_PAGE_VIEW_EVENT_TYPE,
    tenant_id: getSessionManagementServiceInstance().sessionSiteLuid(),
  });
}
